import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import UploadFileIcon from '@mui/icons-material/UploadFile';

const BROWSER_PREVIEW_IMAGE_EXTENSIONS = new Set([
    'avif', 'bmp', 'gif', 'jpeg', 'jpg', 'png', 'svg', 'webp',
]);
const BROWSER_PREVIEW_IMAGE_TYPES = new Set([
    'image/avif', 'image/bmp', 'image/gif', 'image/jpeg', 'image/png', 'image/svg+xml', 'image/webp',
]);

function formatBytes(bytes) {
    if (!Number.isFinite(bytes) || bytes <= 0) return '0 bytes';
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${bytes} bytes`;
}

function isBrowserPreviewableImage(file) {
    if (!file?.image) return false;
    const name = String(file.name || '').trim().toLowerCase();
    const dotIndex = name.lastIndexOf('.');
    if (dotIndex >= 0 && dotIndex < name.length - 1) {
        return BROWSER_PREVIEW_IMAGE_EXTENSIONS.has(name.substring(dotIndex + 1));
    }
    return BROWSER_PREVIEW_IMAGE_TYPES.has(String(file.contentType || '').trim().toLowerCase());
}

function isHeicOrHeif(file) {
    return /\.(heic|heif)$/i.test(String(file?.name || ''))
        || /^image\/(heic|heif)$/i.test(String(file?.contentType || ''));
}

function isMp4Video(file) {
    const name = String(file?.name || '').trim().toLowerCase();
    if (name.endsWith('.mp4')) return true;
    return String(file?.contentType || '').trim().toLowerCase() === 'video/mp4';
}

function isPdfFile(file) {
    const name = String(file?.name || '').trim().toLowerCase();
    if (name.endsWith('.pdf')) return true;
    return String(file?.contentType || '').trim().toLowerCase() === 'application/pdf';
}

export default function WinnersAssetUpload({
    title,
    category,
    files = [],
    maxFiles,
    maxFileSize,
    onChanged,
}) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [pendingDelete, setPendingDelete] = useState([]);
    const [previewIndex, setPreviewIndex] = useState(-1);
    const [videoPreview, setVideoPreview] = useState(null);
    const isPhotoSection = category === 'before_photos';
    const previewImages = files.filter(isBrowserPreviewableImage);
    const nonPreviewFiles = files.filter((file) => !isBrowserPreviewableImage(file));

    const previewHandler = (file) => {
        if (isBrowserPreviewableImage(file)) {
            return () => setPreviewIndex(previewImages.findIndex((image) => image.key === file.key));
        }
        if (isMp4Video(file)) {
            return () => setVideoPreview(file);
        }
        return undefined;
    };

    useEffect(() => {
        const currentKeys = new Set(files.map((file) => file.key));
        setSelectedKeys((current) => current.filter((key) => currentKeys.has(key)));
    }, [files]);

    useEffect(() => {
        if (previewIndex < 0) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setPreviewIndex(-1);
            if (event.key === 'ArrowLeft') setPreviewIndex((current) => Math.max(0, current - 1));
            if (event.key === 'ArrowRight') setPreviewIndex((current) => Math.min(previewImages.length - 1, current + 1));
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [previewIndex, previewImages.length]);

    useEffect(() => {
        if (!videoPreview) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setVideoPreview(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [videoPreview]);

    const uploadFiles = async (event) => {
        const selected = Array.from(event.target.files || []);
        if (inputRef.current) inputRef.current.value = '';
        setError('');
        if (!selected.length) return;
        const normalizedNames = selected.map((file) => file.name.trim().toLocaleLowerCase());
        const duplicateInBatch = normalizedNames.find((name, index) => normalizedNames.indexOf(name) !== index);
        if (duplicateInBatch) {
            const duplicateFile = selected[normalizedNames.lastIndexOf(duplicateInBatch)];
            setError(`${duplicateFile.name} was selected more than once. Choose each file only once.`);
            return;
        }
        const existingNames = new Set(files.map((file) => file.name.trim().toLocaleLowerCase()));
        const existingDuplicate = selected.find((file) => existingNames.has(file.name.trim().toLocaleLowerCase()));
        if (existingDuplicate) {
            setError(`A file named ${existingDuplicate.name} already exists. Delete the existing file first if you intend to replace it.`);
            return;
        }
        if (files.length + selected.length > maxFiles) {
            setError(`You can upload a maximum of ${maxFiles} files in this section.`);
            return;
        }
        const oversized = selected.find((file) => file.size > maxFileSize || file.size <= 0);
        if (oversized) {
            setError(`${oversized.name} must be larger than 0 bytes and no larger than 250 MB.`);
            return;
        }

        setBusy(true);
        try {
            const response = await api.post('/api/winners-assets/upload-urls', {
                category,
                files: selected.map((file) => ({
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream',
                    size: file.size,
                })),
            });
            const uploadTargets = response.data || [];
            for (let index = 0; index < selected.length; index += 1) {
                const file = selected[index];
                await axios.put(uploadTargets[index].uploadUrl, file, {
                    headers: {
                        'Content-Type': file.type || 'application/octet-stream',
                        ...(uploadTargets[index].originalFileNameMetadata
                            ? { 'x-amz-meta-original-filename-b64': uploadTargets[index].originalFileNameMetadata }
                            : {}),
                    },
                });
            }
            await onChanged();
        } catch (uploadError) {
            setError(uploadError?.response?.data?.message || 'Unable to upload the selected files. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = async () => {
        if (!pendingDelete.length) return;
        setBusy(true);
        setError('');
        try {
            await api.delete('/api/winners-assets/assets/bulk', {
                data: { category, keys: pendingDelete.map((file) => file.key) },
            });
            setSelectedKeys([]);
            setPendingDelete([]);
            await onChanged();
        } catch (deleteError) {
            setError(deleteError?.response?.data?.message || 'Unable to delete the selected files. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const toggleSelected = (key) => {
        setSelectedKeys((current) => current.includes(key)
            ? current.filter((selectedKey) => selectedKey !== key)
            : [...current, key]);
    };

    const selectedFiles = files.filter((file) => selectedKeys.includes(file.key));
    const previewFile = previewIndex >= 0 ? previewImages[previewIndex] : null;

    return (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderRadius: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <Box>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700 }}>{title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Maximum {maxFiles} files, 250 MB per file.
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        disabled={busy || selectedFiles.length === 0}
                        onClick={() => setPendingDelete(selectedFiles)}
                    >
                        Delete selected ({selectedFiles.length})
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={busy ? <CircularProgress size={17} color="inherit" /> : <UploadFileIcon />}
                        disabled={busy || files.length >= maxFiles}
                        onClick={() => inputRef.current?.click()}
                    >
                        {busy ? 'Working...' : 'Upload files'}
                    </Button>
                </Box>
                <input ref={inputRef} type="file" multiple hidden onChange={uploadFiles} />
            </Box>

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
            {files.length === 0 && (
                <Typography color="text.secondary" sx={{ mt: 3 }}>No files have been uploaded yet.</Typography>
            )}

            {isPhotoSection ? (
                <>
                    {previewImages.length > 0 && (
                        <Box data-testid="before-photos-thumbnail-grid" sx={{ mt: 3, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 2 }}>
                            {previewImages.map((file) => (
                                <Box key={file.key} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', minWidth: 0 }}>
                                    <Box
                                        component="button"
                                        type="button"
                                        aria-label={`Preview ${file.name}`}
                                        onClick={() => setPreviewIndex(previewImages.findIndex((image) => image.key === file.key))}
                                        sx={{ display: 'block', width: '100%', p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
                                    >
                                        <Box component="img" src={file.url} alt={file.name} sx={{ display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover' }} />
                                    </Box>
                                    <FileFooter
                                        file={file}
                                        busy={busy}
                                        selected={selectedKeys.includes(file.key)}
                                        onSelect={toggleSelected}
                                        onDelete={(item) => setPendingDelete([item])}
                                        onPreview={() => setPreviewIndex(previewImages.findIndex((image) => image.key === file.key))}
                                        compact
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                    {nonPreviewFiles.length > 0 && (
                        <Box data-testid="before-photos-file-rows" sx={{ mt: 3, display: 'grid', gap: 1 }}>
                            {nonPreviewFiles.map((file) => (
                                <FileRow key={file.key} file={file} busy={busy} selected={selectedKeys.includes(file.key)} onSelect={toggleSelected} onDelete={(item) => setPendingDelete([item])} onPreview={previewHandler(file)} />
                            ))}
                        </Box>
                    )}
                </>
            ) : (
                <Box sx={{ mt: 3, display: 'grid', gap: 1 }}>
                    {files.map((file) => (
                        <FileRow
                            key={file.key}
                            file={file}
                            busy={busy}
                            selected={selectedKeys.includes(file.key)}
                            onSelect={toggleSelected}
                            onDelete={(item) => setPendingDelete([item])}
                            onPreview={previewHandler(file)}
                        />
                    ))}
                </Box>
            )}

            <Dialog open={pendingDelete.length > 0} onClose={() => !busy && setPendingDelete([])} maxWidth="sm" fullWidth>
                <DialogTitle>{pendingDelete.length === 1 ? 'Delete file?' : `Delete ${pendingDelete.length} files?`}</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 1 }}>
                        {pendingDelete.length === 1
                            ? 'This file will be permanently deleted:'
                            : 'These files will be permanently deleted:'}
                    </Typography>
                    {pendingDelete.map((file) => (
                        <Typography key={file.key} component="div" sx={{ overflowWrap: 'anywhere', fontWeight: 600, py: 0.5 }}>
                            {file.name}
                        </Typography>
                    ))}
                </DialogContent>
                <DialogActions>
                    <Button disabled={busy} onClick={() => setPendingDelete([])}>Cancel</Button>
                    <Button variant="contained" color="primary" disabled={busy} onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(previewFile)} onClose={() => setPreviewIndex(-1)} maxWidth="xl" fullWidth>
                <DialogTitle>
                    {previewFile ? `${previewFile.name} (${previewIndex + 1} of ${previewImages.length})` : 'Image preview'}
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2, position: 'relative' }}>
                    {previewFile && (
                        <>
                            <IconButton
                                aria-label="Previous image"
                                onClick={() => setPreviewIndex((current) => Math.max(0, current - 1))}
                                disabled={previewIndex <= 0}
                                sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)', border: '1px solid #ddd' }}
                            >
                                <ChevronLeftIcon />
                            </IconButton>
                            <Box component="img" src={previewFile.url} alt={`Preview of ${previewFile.name}`} sx={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }} />
                            <IconButton
                                aria-label="Next image"
                                onClick={() => setPreviewIndex((current) => Math.min(previewImages.length - 1, current + 1))}
                                disabled={previewIndex >= previewImages.length - 1}
                                sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.9)', border: '1px solid #ddd' }}
                            >
                                <ChevronRightIcon />
                            </IconButton>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {previewFile && <Button component="a" href={previewFile.url} target="_blank" rel="noopener noreferrer">Open in new tab</Button>}
                    <Button onClick={() => setPreviewIndex(-1)}>Close</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(videoPreview)} onClose={() => setVideoPreview(null)} maxWidth="md" fullWidth>
                <DialogTitle>{videoPreview ? videoPreview.name : 'Video preview'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
                    {videoPreview && (
                        <video
                            src={videoPreview.url}
                            controls
                            autoPlay
                            preload="metadata"
                            style={{ display: 'block', width: '100%', height: 'auto', maxHeight: '80vh', backgroundColor: '#000' }}
                        >
                            Your browser does not support video playback for this file.
                        </video>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setVideoPreview(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
}

function FileFooter({ file, busy, selected, onSelect, onDelete, onPreview, compact = false }) {
    if (compact) {
        return (
            <Box sx={{ p: 1, minWidth: 0 }}>
                <Typography
                    component="button"
                    type="button"
                    onClick={onPreview}
                    aria-label={`Preview ${file.name} from filename`}
                    variant="body2"
                    title={file.name}
                    sx={{ display: 'block', width: '100%', p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {file.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">{formatBytes(file.size)}</Typography>
                <Box
                    data-testid={`school-preview-card-actions-${file.key}`}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}
                >
                    <Checkbox
                        checked={selected}
                        disabled={busy}
                        onChange={() => onSelect(file.key)}
                        inputProps={{ 'aria-label': `Select ${file.name}` }}
                        size="small"
                    />
                    <Tooltip title="Delete file">
                        <span>
                            <IconButton aria-label={`Delete ${file.name}`} color="error" disabled={busy} onClick={() => onDelete(file)}>
                                <DeleteOutlineIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            </Box>
        );
    }
    return (
        <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Checkbox
                checked={selected}
                disabled={busy}
                onChange={() => onSelect(file.key)}
                inputProps={{ 'aria-label': `Select ${file.name}` }}
                size="small"
            />
            <Box sx={{ minWidth: 0, flex: 1 }}>
                {onPreview ? (
                    <Typography
                        component="button"
                        type="button"
                        onClick={onPreview}
                        aria-label={`Preview ${file.name}`}
                        variant="body2"
                        sx={{ display: 'block', width: '100%', p: 0, border: 0, bgcolor: 'transparent', color: 'primary.main', textAlign: 'left', cursor: 'pointer', overflowWrap: 'anywhere' }}
                    >
                        {file.name}
                    </Typography>
                ) : isHeicOrHeif(file) ? (
                    <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{file.name}</Typography>
                ) : isPdfFile(file) ? (
                    <Typography component="a" href={file.url} target="_blank" rel="noopener noreferrer" variant="body2" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                        {file.name}
                    </Typography>
                ) : (
                    <Typography component="a" href={file.url} variant="body2" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
                        {file.name}
                    </Typography>
                )}
                <Typography variant="caption" color="text.secondary">{formatBytes(file.size)}</Typography>
            </Box>
            <Tooltip title="Delete file">
                <span>
                    <IconButton aria-label={`Delete ${file.name}`} color="error" disabled={busy} onClick={() => onDelete(file)}>
                        <DeleteOutlineIcon />
                    </IconButton>
                </span>
            </Tooltip>
        </Box>
    );
}

function FileRow({ file, busy, selected, onSelect, onDelete, onPreview }) {
    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <FileFooter file={file} busy={busy} selected={selected} onSelect={onSelect} onDelete={onDelete} onPreview={onPreview} />
        </Box>
    );
}